import { FC, useCallback, useContext, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useRuntimeConfig, useSiteConfig } from "@finbackoffice/site-core";
import { CrmContext } from "contexts";
import Translate from "components/base/translate/Translate";
import ActiveLink from "components/base/active-link/ActiveLink";
import { Svg } from "components/base/svg/Svg";
import { AssetsDirectory } from "@finbackoffice/enums";
import { RouteNames } from "utils/constants";
import Button from "components/base/button/Button";
import { HeaderNavButtonsEnum, ISiteConfigLayouts } from "@finbackoffice/fe-core";
import styles from "./header-nav.module.sass";

const HeaderNav: FC = () => {
    const siteLayoutsConfig = useSiteConfig<ISiteConfigLayouts>("layouts");
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { openGamificationUi } = useContext(CrmContext);
    const pathname = usePathname();

    const headerNavigationConfigMemo = useMemo(() => {
        let result = siteLayoutsConfig.headerNavigation;

        if (!COMMON_SITE_CONFIGS.crm.enable || !COMMON_SITE_CONFIGS.crm.config.gamification) {
            result = result.filter((config) => config !== HeaderNavButtonsEnum.Gamification);
        }

        return result;
    }, [
        COMMON_SITE_CONFIGS.crm.config.gamification,
        COMMON_SITE_CONFIGS.crm.enable,
        siteLayoutsConfig,
    ]);

    const renderNavItem = useCallback(
        (name: HeaderNavButtonsEnum) => {
            switch (name) {
                case HeaderNavButtonsEnum.Prematch:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.TOP}`}
                            activeClassName={styles.active}
                            includes={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}`}
                            forceActive={pathname.includes(
                                `/${RouteNames.SPORT}/${RouteNames.PREMATCH}`,
                            )}
                            data-testid="menu-prematch">
                            <Translate tid="pageName_sport" />
                        </ActiveLink>
                    );
                case HeaderNavButtonsEnum.Live:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`}
                            activeClassName={styles.active}
                            includes={`/${RouteNames.SPORT}/${RouteNames.LIVE}`}
                            forceActive={pathname.indexOf(RouteNames.CALENDAR) !== -1}
                            data-testid="menu-live">
                            <Translate tid="pageName_live" />
                        </ActiveLink>
                    );
                case HeaderNavButtonsEnum.Lobby:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.CASINO}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_casino" />
                        </ActiveLink>
                    );
                case HeaderNavButtonsEnum.LiveCasino:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.CASINO}/${RouteNames.LIVE_CASINO}`}
                            includes={`/${RouteNames.CASINO}/${RouteNames.LIVE_CASINO}`}
                            activeClassName={styles.active}
                            data-testid="menu-live-casino">
                            <Translate tid="pageName_livecasino" />
                        </ActiveLink>
                    );
                case HeaderNavButtonsEnum.Slots:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.CASINO}/${RouteNames.SLOTS}`}
                            includes={`/${RouteNames.CASINO}/${RouteNames.SLOTS}`}
                            activeClassName={styles.active}
                            data-testid="menu-slots">
                            <Translate tid="pageName_slots" />
                        </ActiveLink>
                    );
                case HeaderNavButtonsEnum.Bonuses:
                    return (
                        <ActiveLink
                            key={name}
                            href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}`}
                            includes={`/${RouteNames.BONUSES}`}
                            activeClassName={styles.active}
                            data-testid="menu-bonuses">
                            <>
                                <Translate tid="footer_bonuses" />
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/bonuses.svg`}
                                    className={styles.menuIcon}
                                />
                            </>
                        </ActiveLink>
                    );
                case HeaderNavButtonsEnum.Gamification:
                    return (
                        <Button
                            key={name}
                            type="custom"
                            onClick={() => {
                                openGamificationUi();
                            }}
                            data-testid="menu-gamification">
                            <>
                                <Translate tid="header_gamification" />
                                <Svg
                                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-gamification.svg`}
                                    className={styles.menuIcon}
                                />
                            </>
                        </Button>
                    );
                default:
                    return null;
            }
        },
        [openGamificationUi, pathname],
    );

    return <nav className={styles.nav}>{headerNavigationConfigMemo.map(renderNavItem)}</nav>;
};
export default HeaderNav;
